import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Skeleton } from '@/components/ui/skeleton';
import { SEO } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ExternalLink, FileText, Search, RefreshCw, Zap } from 'lucide-react';
import { DynamicSitemapGenerator } from '@/components/seo/DynamicSitemapGenerator';

interface Destination {
  id: string;
  name: string;
  country: string;
}

export const Sitemap = () => {
  const [sitemapContent, setSitemapContent] = useState<string | null>(null);
  const [sitemapFiles, setSitemapFiles] = useState<string[]>(['sitemap.xml']);
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [loadingSitemap, setLoadingSitemap] = useState(true);
  const [loadingDestinations, setLoadingDestinations] = useState(true);
  const [parsedUrlCount, setParsedUrlCount] = useState(0);
  const [activeSitemap, setActiveSitemap] = useState('sitemap.xml');
  
  useEffect(() => {
    const fetchSitemap = async () => {
      try {
        // First check if we have a sitemap index
        const indexResponse = await fetch('/sitemap.xml');
        const indexText = await indexResponse.text();
        setSitemapContent(indexText);
        
        // Count URLs
        const urlMatches = indexText.match(/<url>/g);
        const sitemapMatches = indexText.match(/<sitemap>/g);
        
        if (sitemapMatches && sitemapMatches.length > 0) {
          // We have a sitemap index
          setParsedUrlCount(0);
          
          // Extract sitemap files from the index
          const sitemapRegex = /<loc>(.*?)<\/loc>/g;
          const files: string[] = [];
          let match;
          
          while ((match = sitemapRegex.exec(indexText)) !== null) {
            const url = match[1];
            const filename = url.split('/').pop();
            if (filename) files.push(filename);
          }
          
          if (files.length > 0) {
            setSitemapFiles(files);
          }
        } else if (urlMatches) {
          // Single sitemap file
          setParsedUrlCount(urlMatches.length);
        }
      } catch (error) {
        console.error('Error fetching sitemap:', error);
      } finally {
        setLoadingSitemap(false);
      }
    };
    
    const fetchDestinations = async () => {
      try {
        const { data, error } = await supabase
          .from('destinations')
          .select('id, name, country')
          .order('country')
          .limit(50) // Limit to 50 destinations for performance
          .order('name');
          
        if (error) throw error;
        setDestinations(data || []);
      } catch (error) {
        console.error('Error fetching destinations:', error);
      } finally {
        setLoadingDestinations(false);
      }
    };
    
    fetchSitemap();
    fetchDestinations();
  }, []);
  
  const loadSitemapFile = async (filename: string) => {
    try {
      setLoadingSitemap(true);
      const response = await fetch(`/${filename}`);
      const text = await response.text();
      setSitemapContent(text);
      
      // Count URLs in this sitemap
      const urlMatches = text.match(/<url>/g);
      setParsedUrlCount(urlMatches ? urlMatches.length : 0);
      
      setActiveSitemap(filename);
    } catch (error) {
      console.error(`Error loading sitemap ${filename}:`, error);
    } finally {
      setLoadingSitemap(false);
    }
  };
  
  // Format destination URL
  const formatDestinationUrl = (destination: Destination) => {
    const countrySlug = destination.country.toLowerCase().replace(/\s+/g, '-');
    const nameSlug = destination.name.toLowerCase().replace(/\s+/g, '-');
    return `/destinations/${countrySlug}/${nameSlug}/${destination.id}`;
  };
  
  return (
    <div className="container mx-auto py-12 px-4">
      <SEO 
        title="Sitemap Management"
        description="Complete list of all pages available on Travilis with dynamic sitemap generation"
      />
      
      <h1 className="text-3xl font-bold mb-8">Sitemap Management</h1>
      
      {/* Dynamic Sitemap Generator Section */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Zap className="h-5 w-5 text-yellow-500" />
          <h2 className="text-xl font-semibold">Dynamic Sitemap Generator</h2>
        </div>
        
        <Alert className="mb-4">
          <RefreshCw className="h-4 w-4" />
          <AlertTitle>Automatic SEO Optimization</AlertTitle>
          <AlertDescription>
            Generate fresh sitemaps with real-time data including search pages, country landing pages, 
            and user-generated content for better search engine visibility.
          </AlertDescription>
        </Alert>
        
        <DynamicSitemapGenerator />
      </div>
      
      <Alert className="mb-6">
        <Search className="h-4 w-4" />
        <AlertTitle>SEO Information</AlertTitle>
        <AlertDescription>
          This sitemap is automatically generated and submitted to search engines to help them discover all pages on our website.
          If you're a webmaster, you can submit our sitemap to search engines using the links below.
        </AlertDescription>
      </Alert>
      
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Sitemap Files</h2>
        <div className="flex flex-wrap gap-2 mb-4">
          {sitemapFiles.map((file) => (
            <Button 
              key={file}
              variant={file === activeSitemap ? "default" : "outline"}
              size="sm"
              onClick={() => loadSitemapFile(file)}
            >
              <FileText className="w-4 h-4 mr-2" />
              {file}
            </Button>
          ))}
        </div>
        
        <div className="flex gap-4 mb-4">
          <a 
            href="/sitemap.xml" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="inline-flex items-center text-primary hover:underline"
          >
            View sitemap.xml
            <ExternalLink className="ml-1 h-3 w-3" />
          </a>
          
          <a 
            href="https://search.google.com/search-console" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="inline-flex items-center text-primary hover:underline"
          >
            Submit to Google Search Console
            <ExternalLink className="ml-1 h-3 w-3" />
          </a>
          
          <a 
            href="https://www.bing.com/webmasters/about" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="inline-flex items-center text-primary hover:underline"
          >
            Submit to Bing Webmaster Tools
            <ExternalLink className="ml-1 h-3 w-3" />
          </a>
        </div>
        
        {!loadingSitemap && parsedUrlCount > 0 && (
          <p className="text-sm text-gray-600">
            Found {parsedUrlCount} URLs in {activeSitemap}
          </p>
        )}
        
        {!loadingSitemap && parsedUrlCount === 0 && sitemapFiles.length > 1 && (
          <p className="text-sm text-gray-600">
            This is a sitemap index file containing links to {sitemapFiles.length} sitemap files
          </p>
        )}
      </div>
      
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Main Pages</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link to="/" className="text-primary hover:underline">Homepage</Link>
          <Link to="/search" className="text-primary hover:underline">Search</Link>
          <Link to="/recommendations" className="text-primary hover:underline">Recommendations</Link>
          <Link to="/visa-requirements" className="text-primary hover:underline">Visa Requirements</Link>
          <Link to="/collections" className="text-primary hover:underline">Collections</Link>
          <Link to="/blogs" className="text-primary hover:underline">Blogs</Link>
          <Link to="/trips" className="text-primary hover:underline">Trips</Link>
          <Link to="/hosts" className="text-primary hover:underline">Hosts</Link>
          <Link to="/destinations" className="text-primary hover:underline">Destinations</Link>
          <Link to="/explore" className="text-primary hover:underline">Explore</Link>
          <Link to="/about" className="text-primary hover:underline">About</Link>
          <Link to="/contact" className="text-primary hover:underline">Contact</Link>
          <Link to="/help-center" className="text-primary hover:underline">Help Center</Link>
          <Link to="/safety" className="text-primary hover:underline">Safety</Link>
        </div>
      </div>
      
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Destinations <span className="text-sm text-gray-500">({destinations.length} shown)</span></h2>
        {loadingDestinations ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(9)].map((_, i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
        ) : destinations.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {destinations.map((destination) => (
              <Link 
                key={destination.id} 
                to={formatDestinationUrl(destination)}
                className="text-primary hover:underline"
              >
                {destination.name}, {destination.country}
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No destinations found</p>
        )}
      </div>
      
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Legal Pages</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
          <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
          <Link to="/cookie-policy" className="text-primary hover:underline">Cookie Policy</Link>
          <Link to="/content-policy" className="text-primary hover:underline">Content Policy</Link>
          <Link to="/booking-policy" className="text-primary hover:underline">Booking Policy</Link>
          <Link to="/disclaimer" className="text-primary hover:underline">Disclaimer</Link>
          <Link to="/copyright" className="text-primary hover:underline">Copyright</Link>
        </div>
      </div>
      
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Search Engine Submission</h2>
        <p className="mb-4">
          Submit your sitemap to these search engines to improve your site's visibility:
        </p>
        <div className="space-y-2">
          <div>
            <h3 className="font-medium">Google Search Console</h3>
            <p className="text-sm text-gray-600">
              URL to submit: https://travilis.com/sitemap.xml
            </p>
            <a 
              href="https://search.google.com/search-console" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary text-sm hover:underline"
            >
              Go to Google Search Console →
            </a>
          </div>
          
          <div>
            <h3 className="font-medium">Bing Webmaster Tools</h3>
            <p className="text-sm text-gray-600">
              URL to submit: https://travilis.com/sitemap.xml
            </p>
            <a 
              href="https://www.bing.com/webmasters/about" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary text-sm hover:underline"
            >
              Go to Bing Webmaster Tools →
            </a>
          </div>
          
          <div>
            <h3 className="font-medium">Yandex Webmaster</h3>
            <p className="text-sm text-gray-600">
              URL to submit: https://travilis.com/sitemap.xml
            </p>
            <a 
              href="https://webmaster.yandex.com/welcome/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary text-sm hover:underline"
            >
              Go to Yandex Webmaster →
            </a>
          </div>
        </div>
      </div>
      
      {loadingSitemap ? (
        <div className="text-center">
          <Skeleton className="h-40 w-full" />
          <p className="mt-2 text-gray-500">Loading sitemap content...</p>
        </div>
      ) : (
        <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
          <h3 className="text-lg font-medium mb-2">Sitemap Content Preview</h3>
          <div className="text-sm mb-2">
            <p className="text-gray-500">
              Showing first {Math.min(sitemapContent?.length || 0, 2000)} characters of {sitemapContent?.length || 0} total
            </p>
          </div>
          <pre className="overflow-x-auto text-xs whitespace-pre-wrap max-h-96 overflow-y-auto p-4 bg-white border border-gray-100 rounded">
            {sitemapContent?.substring(0, 2000) || 'Failed to load sitemap content'}
            {(sitemapContent?.length || 0) > 2000 && '... (content truncated)'}
          </pre>
        </div>
      )}
    </div>
  );
};

export default Sitemap;
